import { isEqual } from 'lodash';
import { type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type BreadcrumbsItemType } from '@air/component-breadcrumbs';
import { BoardIcon } from '@air/design-icons';

import {
  CentralizedBoardSelectors,
  centralizedBoardAncestorsSelector,
  centralizedBoardTitleSelector,
} from '~/store/centralizedBoard/selectors';
import { type LocationBoard } from '~/store/router/types';

type UseBoardLocationBreadcrumbsOptions = {
  shouldShowBoardIcon: boolean;
  onBoardClick: (board: LocationBoard) => void;
  boardSuffixContent?: ReactNode;
};

export const useBoardLocationBreadcrumbs = ({
  shouldShowBoardIcon,
  onBoardClick,
  boardSuffixContent,
}: UseBoardLocationBreadcrumbsOptions): BreadcrumbsItemType[] => {
  const ancestors = useSelector(centralizedBoardAncestorsSelector, isEqual);
  const title = useSelector(centralizedBoardTitleSelector);
  const id = useSelector(CentralizedBoardSelectors.boardId);

  const breadcrumbItems = useMemo(() => {
    const items: BreadcrumbsItemType[] = [];

    // Add ancestor boards
    ancestors.forEach((ancestor, index) => {
      const isFirstAncestor = index === 0;

      items.push({
        id: ancestor.id,
        label: ancestor.title,
        prefix:
          isFirstAncestor && shouldShowBoardIcon ? (
            <BoardIcon size="sm" className="ads:text-icon-default text-gray-10" />
          ) : undefined,
        className: 'max-w-[170px]',
        onClick: () => onBoardClick({ id: ancestor.id, title: ancestor.title }),
        type: 'button',
      });
    });

    // Add current board
    if (id && title) {
      const hasAncestors = ancestors?.length;

      items.push({
        id,
        className: 'max-w-[350px] group/copy-path',
        prefix:
          !hasAncestors && shouldShowBoardIcon ? (
            <BoardIcon size="sm" className="ads:text-icon-default text-gray-10" />
          ) : undefined,
        label: title,
        suffix: boardSuffixContent,
        type: 'item',
      });
    }

    return items;
  }, [ancestors, id, title, shouldShowBoardIcon, onBoardClick, boardSuffixContent]);

  return breadcrumbItems;
};
